import React, { useState, useEffect, useRef } from 'react'
import { messagingService, Message, MessageThread } from '../lib/messagingService'
import { supabase } from '../lib/supabase'
import { useEventYear } from './EventYearContext'
import { User } from './AuthContext'
import { AddParticipants } from './AddParticipants'

interface GroupThreadChatProps {
  threadId: string
  currentUser: User
}

export const GroupThreadChat: React.FC<GroupThreadChatProps> = ({ threadId, currentUser }) => {
  const { currentYear } = useEventYear()
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [thread, setThread] = useState<MessageThread | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [messageAreaHeight, setMessageAreaHeight] = useState(() => {
    const saved = localStorage.getItem(`group-thread-height-${threadId}`)
    return saved ? parseInt(saved) : 400
  })
  const [isResizing, setIsResizing] = useState(false)
  const [showAddParticipants, setShowAddParticipants] = useState(false)
  const [participantCount, setParticipantCount] = useState(0)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const subscriptionRef = useRef<any>(null)
  const resizeStartY = useRef<number>(0)
  const startHeight = useRef<number>(0)
  
  const isPRTeam = currentUser.role === 'pr' || currentUser.role === 'admin'

  // Detect if user is on mobile device
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
           window.innerWidth <= 768
  }

  // Scroll to bottom of messages
  const scrollToBottom = (immediate = false) => {
    console.log('scrollToBottom called, immediate:', immediate, 'isMobile:', isMobile())
    
    // Use longer delay for mobile to ensure DOM is ready
    const delay = isMobile() ? (immediate ? 200 : 100) : (immediate ? 100 : 50)
    
    setTimeout(() => {
      const element = messagesEndRef.current
      if (element) {
        const container = element.parentElement
        if (container) {
          // For mobile, always use direct scrollTop for reliability
          if (isMobile()) {
            container.scrollTop = container.scrollHeight + 1000 // Add extra to ensure we're at bottom
            console.log('Mobile scroll: scrollTop set to', container.scrollHeight)
          } else {
            // Desktop can use smooth scrolling
            element.scrollIntoView({ 
              behavior: immediate ? 'instant' : 'smooth',
              block: 'end'
            })
            // Also set scrollTop as backup
            container.scrollTop = container.scrollHeight
          }
          console.log('Scroll completed, scrollTop:', container.scrollTop, 'scrollHeight:', container.scrollHeight)
        }
      }
    }, delay)
  }

  // Handle resize start
  const handleResizeStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault()
    setIsResizing(true)
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    resizeStartY.current = clientY
    startHeight.current = messageAreaHeight
  }

  // Handle resize move
  const handleResizeMove = (e: MouseEvent | TouchEvent) => {
    if (!isResizing) return
    
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
    const deltaY = clientY - resizeStartY.current // Reversed: positive = drag down = bigger
    const newHeight = Math.max(200, Math.min(800, startHeight.current + deltaY))
    
    setMessageAreaHeight(newHeight)
  }

  // Handle resize end
  const handleResizeEnd = () => {
    if (!isResizing) return
    setIsResizing(false)
    localStorage.setItem(`group-thread-height-${threadId}`, messageAreaHeight.toString())
  }

  // Add event listeners for resize
  useEffect(() => {
    if (isResizing) {
      const handleMouseMove = (e: MouseEvent) => handleResizeMove(e)
      const handleMouseUp = () => handleResizeEnd()
      const handleTouchMove = (e: TouchEvent) => handleResizeMove(e)
      const handleTouchEnd = () => handleResizeEnd()

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('touchmove', handleTouchMove)
      document.addEventListener('touchend', handleTouchEnd)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleTouchEnd)
      }
    }
  }, [isResizing, messageAreaHeight, threadId])

  // Load participant count
  const loadParticipantCount = async () => {
    const participants = await messagingService.getThreadParticipants(threadId)
    setParticipantCount(participants.length)
  }

  // Load thread and messages
  useEffect(() => {
    let mounted = true

    const loadThread = async () => {
      setIsLoading(true)
      
      // Get thread details
      const { data: threadData, error: threadError } = await supabase
        .from('message_threads')
        .select('*')
        .eq('id', threadId)
        .single()
      
      if (!mounted) return
      
      if (threadError || !threadData) {
        console.error('Error loading thread:', threadError)
        setIsLoading(false)
        return
      }

      setThread(threadData)
      
      // Load participant count
      await loadParticipantCount()
      
      // Load existing messages
      const existingMessages = await messagingService.getMessagesForThread(threadId, currentYear)

      if (mounted) {
        setMessages(existingMessages)

        // Mark thread as read
        await messagingService.markThreadAsRead(threadId, currentUser.id)

        // Subscribe to real-time updates
        subscriptionRef.current = messagingService.subscribeToThread(
          threadId,
          (newMessage: Message) => {
            if (mounted) {
              setMessages(prev => [...prev, newMessage])

              // Auto-mark as read if user is viewing
              messagingService.markThreadAsRead(threadId, currentUser.id)
            }
          }
        )
      }

      if (mounted) {
        setIsLoading(false)
      }
    }

    loadThread()

    return () => {
      mounted = false
      if (subscriptionRef.current) {
        messagingService.unsubscribe(subscriptionRef.current)
      }
    }
  }, [threadId, currentUser.id, currentYear])

  // Scroll to bottom when messages change
  useEffect(() => {
    console.log('Messages changed, scrolling. Message count:', messages.length)
    if (messages.length > 0) {
      scrollToBottom()
    }
  }, [messages])

  // Scroll to bottom when thread loads
  useEffect(() => {
    console.log('Thread/threadId changed. Thread:', thread?.title, 'ThreadID:', threadId, 'Message count:', messages.length)
    if (thread && messages.length > 0) {
      console.log('Triggering immediate scroll for thread entry')
      scrollToBottom(true) // Immediate scroll when entering conversation
    }
  }, [thread, threadId])

  // Additional effect to ensure scroll after all loading is complete
  useEffect(() => {
    if (!isLoading && thread && messages.length > 0) {
      console.log('Loading complete, ensuring scroll to bottom')
      // Extra scroll attempts for mobile reliability
      if (isMobile()) {
        // Multiple scroll attempts to handle various mobile browser quirks
        scrollToBottom(true)
        setTimeout(() => scrollToBottom(true), 300)
        setTimeout(() => scrollToBottom(true), 500)
        setTimeout(() => scrollToBottom(true), 1000)
      } else {
        setTimeout(() => {
          scrollToBottom(true)
        }, 200)
      }
    }
  }, [isLoading, thread, messages.length])

  // Send message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!newMessage.trim() || !thread || isSending) return
    
    setIsSending(true)
    
    try {
      const sentMessage = await messagingService.sendMessage(
        thread.id,
        currentUser.id,
        newMessage,
        false,
        currentYear
      )
      
      if (sentMessage) {
        setNewMessage('')
        // Message will be added via real-time subscription
      }
    } catch (error) {
      console.error('Error sending message:', error)
      alert('Failed to send message. Please try again.')
    } finally {
      setIsSending(false)
    }
  }

  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  // Format date for message grouping
  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday'
    } else {
      return date.toLocaleDateString()
    }
  }

  // Handler for when participants are added
  const handleParticipantsAdded = () => {
    setShowAddParticipants(false)
    // Reload participant count
    loadParticipantCount()
  }

  // Group messages by date
  const groupedMessages = messages.reduce((groups: { [key: string]: Message[] }, message) => {
    const date = formatDate(message.created_at)
    if (!groups[date]) {
      groups[date] = []
    }
    groups[date].push(message)
    return groups
  }, {})

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading conversation...</div>
      </div>
    )
  }

  if (!thread) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Conversation not found</div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-white max-h-screen min-h-0">
      {/* Header */}
      <div className="flex-none p-4 border-b border-gray-200 bg-blue-50">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-blue-700">👥 {thread.title}</h2>
            {thread.description && (
              <p className="text-sm text-gray-600 mt-1">{thread.description}</p>
            )}
            {participantCount > 0 && (
              <p className="text-xs text-gray-500 mt-1">{participantCount} participants</p>
            )}
          </div>
          {isPRTeam && (
            <button
              onClick={() => setShowAddParticipants(true)}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 touch-manipulation flex-shrink-0"
              title="Manage participants"
            >
              Manage
            </button>
          )}
          {!isPRTeam && (
            <div className="px-3 py-1.5 text-xs text-gray-500">
              {currentUser.role} - {participantCount} members
            </div>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div 
        className="overflow-y-auto p-4 space-y-4"
        style={{ height: `${messageAreaHeight}px` }}
      >
        {Object.entries(groupedMessages).map(([date, dateMessages]) => (
          <div key={date}>
            {/* Date Separator */}
            <div className="flex items-center my-4">
              <div className="flex-1 border-t border-gray-200"></div>
              <div className="px-3 text-sm text-gray-500 bg-white">{date}</div>
              <div className="flex-1 border-t border-gray-200"></div>
            </div>

            {/* Messages for this date */}
            {dateMessages.map((message) => {
              const isOwnMessage = message.sender_id === currentUser.id
              const senderName = message.sender?.name || 'Unknown User'
              const senderRole = message.sender?.role || ''
              const senderOutlet = message.sender?.outlet || ''

              return (
                <div key={message.id} className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg shadow ${
                    isOwnMessage 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 text-gray-900'
                  }`}>
                    {/* Sender info (only for others' messages) */}
                    {!isOwnMessage && (
                      <div className="text-xs text-gray-600 mb-1">
                        <strong>{senderName}</strong>
                        {senderRole && ['pr', 'admin'].includes(senderRole) && (
                          <span className="ml-1">(PR Team)</span>
                        )}
                        {senderRole === 'journalist' && senderOutlet && (
                          <span className="ml-1">({senderOutlet})</span>
                        )}
                      </div>
                    )}
                    
                    {/* Message content */}
                    <div className="break-words">{message.content}</div>
                    
                    {/* Timestamp */}
                    <div className={`text-xs mt-1 ${
                      isOwnMessage ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {formatTime(message.created_at)}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Resize Handle */}
      <div 
        className={`flex-none h-3 sm:h-2 border-t border-gray-200 cursor-ns-resize hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation ${isResizing ? 'bg-gray-200' : ''}`}
        onMouseDown={handleResizeStart}
        onTouchStart={handleResizeStart}
        title="Drag to resize message area"
      >
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-12 sm:w-8 h-1 sm:h-0.5 bg-gray-400 rounded"></div>
        </div>
      </div>

      {/* Message Input */}
      <div className="flex-none p-4 border-t border-gray-200 bg-gray-50">
        <form onSubmit={handleSendMessage} className="flex space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 px-3 py-3 sm:py-2 text-base sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation"
            disabled={isSending}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || isSending}
            className="px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation text-base sm:text-sm font-medium"
          >
            {isSending ? 'Sending...' : 'Send'}
          </button>
        </form>
      </div>

      {/* Add Participants Modal */}
      {showAddParticipants && thread && (
        <AddParticipants
          threadId={threadId}
          threadTitle={thread.title}
          currentUser={currentUser}
          onParticipantsAdded={handleParticipantsAdded}
          onCancel={() => setShowAddParticipants(false)}
        />
      )}
    </div>
  )
}